// WHICH KINDS TRAVEL TOGETHER: the store's kinds in groups, so a route is sent
// only the groups it reads. Shared by the server, which reads a group's rows
// (store-db.ts), and the browser, whose replace and hydration are per group
// (lib/store-hydrate.ts). It imports nothing, so the server can import it without dragging a client
// store into its bundle — and so tools/ can run it under bare node (it is in
// SCHEMA_CODE because store-db.ts imports it; erasable TypeScript only).
//
// Which ROUTE reads which group is a second table, lib/route-store-groups.ts:
// this file says what a group is, that one says who needs it.
//
// ─── THE GROUPS ────────────────────────────────────────────────────────────
//
//   - `scripts`: what the two script pages render their lists from — the
//     drafts, their stage overrides, the footage stages and the approval dates
//     (and the grades, by diff only: below). ⚠️ EVERY DRAFT IN ONE GROUP: the
//     draft-id counter is bumped past every draft id the tab has seen
//     (ironside-script-store bumpDraftCounterFor), so a tab holding half the
//     drafts could mint an id that exists.
//   - `schedule`: the schedule kinds (cadence, dates, plans, commitments, the
//     approval seat), all the Schedule page reads (it was ~7 KB of the
//     ~800 KB `scripts` it used to be sent). ONE WAY ONLY:
//     the script store reads the schedule store (a brand's seat, a week's plan,
//     in every stage move), and the schedule store reads nothing of the
//     scripts, so every page that reads `scripts` reads `schedule` too
//     (lib/route-store-groups.ts), and the Schedule page reads it alone.
//   - `editor`: the comments, the Drive footage and the undo points, which only
//     the script editor shows or moves. The Videos page never reads
//     them. A tab without them must never retract them by what it holds, so a
//     deleted script's footage and undo points are the SERVER's to retract
//     (app/api/store/route.ts retractEditorRows, and Delete week's own
//     route); its comments are kept.
//   - `consoles`: the star ratings, the grader feedback and the grade
//     corrections — the /feedback console and the graded-run page.
//   - `rubric`: the SME correction rails' comments, on the rubric and the three
//     skill pages (and the /feedback console, which lists them).
//
// ─── AND WHAT NO DUMP CARRIES ──────────────────────────────────────────────
//
//   - `grade` is in `scripts`, but no dump carries it: it was
//     1.86 MB of the ~3 MB every hard load carried, and the editor asks for the
//     ones it shows (GET /api/scripts/editor/grade). A tab holding `scripts` gets
//     every grade CHANGE in its diff, so the grades it holds stay current for as
//     long as its cursor holds — and asks again when a dump of `scripts` resets
//     it (forgetGradeKnowledge).
//   - `notify_mark` is in no group: written and read server-side only
//     (submit-notice.ts). A tab that names its groups is never sent one; the
//     whole-store answer below (no groups named) still leaves it out of a dump
//     and sends it in a diff, as it always did.
//
// ⚠️ A KIND IN NO GROUP IS NEVER SENT TO A TAB THAT NAMES ITS GROUPS, which is
// every tab now. So a new kind a client store reads must be added to a
// group here; tests/unit/store-groups.test.ts fails on a STORE_KIND_ROUTE kind
// (every kind the client writes) that is in none.

/** A set of kinds sent and held together. */
export type StoreGroup = "scripts" | "schedule" | "editor" | "consoles" | "rubric";

/** Every group, in a fixed order (the order a query param lists them in). */
export const STORE_GROUPS: readonly StoreGroup[] = ["scripts", "schedule", "editor", "consoles", "rubric"];

/** The kinds a group's DUMP carries — a seed, a group load, a full answer. */
const DUMP_KINDS: Readonly<Record<StoreGroup, readonly string[]>> = {
  scripts: ["draft", "stage_override", "video_stage", "approved_on"],
  schedule: ["week_date", "week_plan", "brand_schedule", "brand_commitment", "brand_approval"],
  editor: ["comment", "drive_video", "stage_undo", "week_assignment"],
  consoles: ["feedback", "grader_feedback", "run_comment"],
  rubric: ["rubric_comment", "skill_comment", "affiliate_skill_comment", "client_slack_skill_comment"],
};

/** Kinds a group holds that its dump leaves out: only its DIFF carries them (the header). */
const DIFF_ONLY_KINDS: Readonly<Record<StoreGroup, readonly string[]>> = {
  scripts: ["grade"],
  schedule: [],
  editor: [],
  consoles: [],
  rubric: [],
};

/**
 * The kinds the WHOLE-STORE dump leaves out: GET /api/store with no groups
 * named, which is what an old tab and `smoke.spec.ts` ask (getAllRows). A
 * denylist, as it was before the groups, so that answer is unchanged: every
 * diff-only kind, and the server-side kinds no group holds.
 */
export const KINDS_NOT_IN_DUMP: ReadonlySet<string> = new Set([
  ...STORE_GROUPS.flatMap((g) => DIFF_ONLY_KINDS[g]),
  "notify_mark",
  // Setup receipts stay server-side. Assignment snapshots are only hydrated
  // by the editor, not the old whole-store dump or the Videos board.
  "week_assignment",
  "script_schedule",
]);

/** The kinds a dump of these groups carries. */
export function dumpKindsOf(groups: readonly StoreGroup[]): string[] {
  return groups.flatMap((g) => DUMP_KINDS[g]);
}

/** The kinds a diff over these groups carries: their dumps' kinds and the diff-only ones. */
export function diffKindsOf(groups: readonly StoreGroup[]): string[] {
  return groups.flatMap((g) => [...DUMP_KINDS[g], ...DIFF_ONLY_KINDS[g]]);
}

/** The group a kind belongs to, dumped or diff-only; null for a kind no group holds. */
export function groupOfKind(kind: string): StoreGroup | null {
  for (const g of STORE_GROUPS) if (DUMP_KINDS[g].includes(kind) || DIFF_ONLY_KINDS[g].includes(kind)) return g;
  return null;
}

/**
 * A query param's groups: null when the param is ABSENT (the whole store, as
 * before), and the named groups otherwise — `""` is "none", a real answer for
 * a page that reads no store. A name that is no group is dropped, not refused:
 * a tab from a later build may know one this server does not.
 */
export function parseGroups(raw: string | null): StoreGroup[] | null {
  if (raw === null) return null;
  const named = new Set(raw.split(","));
  return STORE_GROUPS.filter((g) => named.has(g));
}

/** Groups as a query param's value, in STORE_GROUPS order, each once. */
export function formatGroups(groups: Iterable<StoreGroup>): string {
  const set = new Set(groups);
  return STORE_GROUPS.filter((g) => set.has(g)).join(",");
}

/** The groups in either list, each once, in STORE_GROUPS order. */
export function unionGroups(a: Iterable<StoreGroup>, b: Iterable<StoreGroup>): StoreGroup[] {
  const set = new Set([...a, ...b]);
  return STORE_GROUPS.filter((g) => set.has(g));
}

/**
 * The most grades one GET /api/scripts/editor/grade may ask for. A week is five
 * videos; a larger ask is not the editor's, and the route refuses it rather than
 * reading it. The browser's batcher splits at the same number.
 */
export const MAX_GRADES_PER_ASK = 20;
